import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mic } from 'lucide-react';
import { API } from '@/App';

/**
 * Shared visual chrome for the three legal pages (Privacy Policy,
 * Terms of Service, HIPAA Compliance). Keeps brand consistency and
 * surfaces cross-links between the policies in the page footer so
 * regulators / counsel can navigate the full set quickly.
 *
 * `policyKey` ('privacy' | 'terms' | 'hipaa') triggers an auto-detect
 * lookup against `/api/legal/policies-meta`, which returns the git
 * last-commit date of the corresponding source file (falling back to
 * filesystem mtime). The hardcoded `lastUpdated` prop is used only as
 * an SSR-style placeholder until the fetch resolves — keeps the page
 * useful even if the API is briefly unreachable.
 */
export default function LegalLayout({ title, effectiveDate, lastUpdated, policyKey, children }) {
  const [autoLastUpdated, setAutoLastUpdated] = useState(null);

  useEffect(() => {
    if (!policyKey) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/legal/policies-meta`);
        const detected = res.data?.[policyKey]?.last_updated;
        if (!cancelled && detected) setAutoLastUpdated(detected);
      } catch (_e) {
        // Silently fall back to the prop — no UI noise on legal pages
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [policyKey]);

  // Format YYYY-MM-DD → "May 2, 2026" so the legal page reads naturally
  const displayLastUpdated = (() => {
    const raw = autoLastUpdated || lastUpdated;
    if (!raw) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!m) return raw; // already pre-formatted by the page
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  })();
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2.5 group" data-testid="legal-home-link">
            <div className="w-8 h-8 rounded-full bg-[#2C4A3B] flex items-center justify-center">
              <Mic className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold tracking-tight">Plexura AI Scribe</span>
          </Link>
          <Link
            to="/landing"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-[#2C4A3B] transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Legal</p>
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="legal-title"
          >
            {title}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-stone-500">
            {effectiveDate && (
              <span>
                <span className="font-medium text-stone-700">Effective:</span> {effectiveDate}
              </span>
            )}
            {displayLastUpdated && (
              <span data-testid="legal-last-updated">
                <span className="font-medium text-stone-700">Last updated:</span> {displayLastUpdated}
              </span>
            )}
          </div>
        </div>

        <article className="prose prose-stone max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-[#1C1917] prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-[#2C4A3B] prose-p:text-stone-700 prose-p:leading-relaxed prose-li:text-stone-700 prose-li:leading-relaxed prose-strong:text-[#1C1917] prose-a:text-[#2C4A3B] prose-a:underline">
          {children}
        </article>

        {/* Cross-links */}
        <div className="mt-20 pt-10 border-t border-stone-200">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-400 mb-4">Other legal documents</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/legal/privacy"
              className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-stone-200 text-sm font-medium text-stone-700 hover:border-[#2C4A3B] hover:text-[#2C4A3B] transition-colors"
              data-testid="cross-link-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms"
              className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-stone-200 text-sm font-medium text-stone-700 hover:border-[#2C4A3B] hover:text-[#2C4A3B] transition-colors"
              data-testid="cross-link-terms"
            >
              Terms of Service
            </Link>
            <Link
              to="/legal/hipaa"
              className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-stone-200 text-sm font-medium text-stone-700 hover:border-[#2C4A3B] hover:text-[#2C4A3B] transition-colors"
              data-testid="cross-link-hipaa"
            >
              HIPAA Compliance
            </Link>
          </div>
          <p className="mt-8 text-xs text-stone-500 leading-relaxed">
            Questions, requests, or concerns regarding this document &mdash; including legal,
            privacy, individual-rights, security, and HIPAA matters &mdash; can be sent to{' '}
            <a href="mailto:info@plexura.ai" className="text-[#2C4A3B] underline">info@plexura.ai</a>.
          </p>
        </div>
      </main>

      <footer className="border-t border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-xs text-stone-500">&copy; {new Date().getFullYear()} Plexura AI Scribe. All rights reserved.</p>
          <p className="text-xs text-stone-500">HIPAA Compliant &middot; Encrypted &middot; Secure</p>
        </div>
      </footer>
    </div>
  );
}
